package kpt.core.database.crypto.dao

import androidx.room3.EntityDeleteOrUpdateAdapter
import androidx.room3.EntityInsertAdapter
import androidx.room3.EntityUpsertAdapter
import androidx.room3.RoomDatabase
import androidx.room3.coroutines.createFlow
import androidx.room3.util.getColumnIndexOrThrow
import androidx.room3.util.performInTransactionSuspending
import androidx.room3.util.performSuspending
import androidx.sqlite.SQLiteStatement
import kotlin.Double
import kotlin.Int
import kotlin.Long
import kotlin.String
import kotlin.Suppress
import kotlin.Unit
import kotlin.collections.List
import kotlin.collections.MutableList
import kotlin.collections.mutableListOf
import kotlin.reflect.KClass
import kotlinx.coroutines.flow.Flow
import kpt.core.database.crypto.entity.CoinMarketEntity

@Suppress(names = ["UNCHECKED_CAST", "DEPRECATION", "REDUNDANT_PROJECTION", "REMOVAL", "OPT_IN_USAGE_ERROR", "OPT_IN_USAGE", "MemberExtensionConflict", "CAN_BE_VAL"])
internal class CoinMarketDao_Impl(
  __db: RoomDatabase,
) : CoinMarketDao {
  private val __db: RoomDatabase

  private val __upsertAdapterOfCoinMarketEntity: EntityUpsertAdapter<CoinMarketEntity>
  init {
    this.__db = __db
    this.__upsertAdapterOfCoinMarketEntity = EntityUpsertAdapter<CoinMarketEntity>(object : EntityInsertAdapter<CoinMarketEntity>() {
      protected override fun createQuery(): String = "INSERT INTO `coin_markets` (`id`,`symbol`,`name`,`imageUrl`,`currentPrice`,`marketCap`,`marketCapRank`,`priceChangePercent24h`,`high24h`,`low24h`,`page`,`fetchedAt`) VALUES (?,?,?,?,?,?,?,?,?,?,?,?)"

      protected override fun bind(statement: SQLiteStatement, entity: CoinMarketEntity) {
        statement.bindText(1, entity.id)
        statement.bindText(2, entity.symbol)
        statement.bindText(3, entity.name)
        statement.bindText(4, entity.imageUrl)
        statement.bindDouble(5, entity.currentPrice)
        statement.bindLong(6, entity.marketCap)
        statement.bindLong(7, entity.marketCapRank.toLong())
        statement.bindDouble(8, entity.priceChangePercent24h)
        statement.bindDouble(9, entity.high24h)
        statement.bindDouble(10, entity.low24h)
        statement.bindLong(11, entity.page.toLong())
        statement.bindLong(12, entity.fetchedAt)
      }
    }, object : EntityDeleteOrUpdateAdapter<CoinMarketEntity>() {
      protected override fun createQuery(): String = "UPDATE `coin_markets` SET `id` = ?,`symbol` = ?,`name` = ?,`imageUrl` = ?,`currentPrice` = ?,`marketCap` = ?,`marketCapRank` = ?,`priceChangePercent24h` = ?,`high24h` = ?,`low24h` = ?,`page` = ?,`fetchedAt` = ? WHERE `id` = ?"

      protected override fun bind(statement: SQLiteStatement, entity: CoinMarketEntity) {
        statement.bindText(1, entity.id)
        statement.bindText(2, entity.symbol)
        statement.bindText(3, entity.name)
        statement.bindText(4, entity.imageUrl)
        statement.bindDouble(5, entity.currentPrice)
        statement.bindLong(6, entity.marketCap)
        statement.bindLong(7, entity.marketCapRank.toLong())
        statement.bindDouble(8, entity.priceChangePercent24h)
        statement.bindDouble(9, entity.high24h)
        statement.bindDouble(10, entity.low24h)
        statement.bindLong(11, entity.page.toLong())
        statement.bindLong(12, entity.fetchedAt)
        statement.bindText(13, entity.id)
      }
    })
  }

  public override suspend fun replacePage(page: Int, entities: List<CoinMarketEntity>): Unit = performInTransactionSuspending(__db) {
    super@CoinMarketDao_Impl.replacePage(page, entities)
  }

  public override suspend fun upsertAll(entities: List<CoinMarketEntity>): Unit = performSuspending(__db, false, true) { _connection ->
    __upsertAdapterOfCoinMarketEntity.upsert(_connection, entities)
  }

  public override fun getPage(limit: Int, offset: Int): Flow<List<CoinMarketEntity>> {
    val _sql: String = "SELECT * FROM coin_markets ORDER BY marketCapRank ASC LIMIT ? OFFSET ?"
    return createFlow(__db, false, arrayOf("coin_markets")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, limit.toLong())
        _argIndex = 2
        _stmt.bindLong(_argIndex, offset.toLong())
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSymbol: Int = getColumnIndexOrThrow(_stmt, "symbol")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfImageUrl: Int = getColumnIndexOrThrow(_stmt, "imageUrl")
        val _columnIndexOfCurrentPrice: Int = getColumnIndexOrThrow(_stmt, "currentPrice")
        val _columnIndexOfMarketCap: Int = getColumnIndexOrThrow(_stmt, "marketCap")
        val _columnIndexOfMarketCapRank: Int = getColumnIndexOrThrow(_stmt, "marketCapRank")
        val _columnIndexOfPriceChangePercent24h: Int = getColumnIndexOrThrow(_stmt, "priceChangePercent24h")
        val _columnIndexOfHigh24h: Int = getColumnIndexOrThrow(_stmt, "high24h")
        val _columnIndexOfLow24h: Int = getColumnIndexOrThrow(_stmt, "low24h")
        val _columnIndexOfPage: Int = getColumnIndexOrThrow(_stmt, "page")
        val _columnIndexOfFetchedAt: Int = getColumnIndexOrThrow(_stmt, "fetchedAt")
        val _result: MutableList<CoinMarketEntity> = mutableListOf()
        while (_stmt.step()) {
          val _item: CoinMarketEntity
          val _tmpId: String
          _tmpId = _stmt.getText(_columnIndexOfId)
          val _tmpSymbol: String
          _tmpSymbol = _stmt.getText(_columnIndexOfSymbol)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpImageUrl: String
          _tmpImageUrl = _stmt.getText(_columnIndexOfImageUrl)
          val _tmpCurrentPrice: Double
          _tmpCurrentPrice = _stmt.getDouble(_columnIndexOfCurrentPrice)
          val _tmpMarketCap: Long
          _tmpMarketCap = _stmt.getLong(_columnIndexOfMarketCap)
          val _tmpMarketCapRank: Int
          _tmpMarketCapRank = _stmt.getLong(_columnIndexOfMarketCapRank).toInt()
          val _tmpPriceChangePercent24h: Double
          _tmpPriceChangePercent24h = _stmt.getDouble(_columnIndexOfPriceChangePercent24h)
          val _tmpHigh24h: Double
          _tmpHigh24h = _stmt.getDouble(_columnIndexOfHigh24h)
          val _tmpLow24h: Double
          _tmpLow24h = _stmt.getDouble(_columnIndexOfLow24h)
          val _tmpPage: Int
          _tmpPage = _stmt.getLong(_columnIndexOfPage).toInt()
          val _tmpFetchedAt: Long
          _tmpFetchedAt = _stmt.getLong(_columnIndexOfFetchedAt)
          _item = CoinMarketEntity(_tmpId,_tmpSymbol,_tmpName,_tmpImageUrl,_tmpCurrentPrice,_tmpMarketCap,_tmpMarketCapRank,_tmpPriceChangePercent24h,_tmpHigh24h,_tmpLow24h,_tmpPage,_tmpFetchedAt)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun getAll(): Flow<List<CoinMarketEntity>> {
    val _sql: String = "SELECT * FROM coin_markets ORDER BY marketCapRank ASC"
    return createFlow(__db, false, arrayOf("coin_markets")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSymbol: Int = getColumnIndexOrThrow(_stmt, "symbol")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfImageUrl: Int = getColumnIndexOrThrow(_stmt, "imageUrl")
        val _columnIndexOfCurrentPrice: Int = getColumnIndexOrThrow(_stmt, "currentPrice")
        val _columnIndexOfMarketCap: Int = getColumnIndexOrThrow(_stmt, "marketCap")
        val _columnIndexOfMarketCapRank: Int = getColumnIndexOrThrow(_stmt, "marketCapRank")
        val _columnIndexOfPriceChangePercent24h: Int = getColumnIndexOrThrow(_stmt, "priceChangePercent24h")
        val _columnIndexOfHigh24h: Int = getColumnIndexOrThrow(_stmt, "high24h")
        val _columnIndexOfLow24h: Int = getColumnIndexOrThrow(_stmt, "low24h")
        val _columnIndexOfPage: Int = getColumnIndexOrThrow(_stmt, "page")
        val _columnIndexOfFetchedAt: Int = getColumnIndexOrThrow(_stmt, "fetchedAt")
        val _result: MutableList<CoinMarketEntity> = mutableListOf()
        while (_stmt.step()) {
          val _item: CoinMarketEntity
          val _tmpId: String
          _tmpId = _stmt.getText(_columnIndexOfId)
          val _tmpSymbol: String
          _tmpSymbol = _stmt.getText(_columnIndexOfSymbol)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpImageUrl: String
          _tmpImageUrl = _stmt.getText(_columnIndexOfImageUrl)
          val _tmpCurrentPrice: Double
          _tmpCurrentPrice = _stmt.getDouble(_columnIndexOfCurrentPrice)
          val _tmpMarketCap: Long
          _tmpMarketCap = _stmt.getLong(_columnIndexOfMarketCap)
          val _tmpMarketCapRank: Int
          _tmpMarketCapRank = _stmt.getLong(_columnIndexOfMarketCapRank).toInt()
          val _tmpPriceChangePercent24h: Double
          _tmpPriceChangePercent24h = _stmt.getDouble(_columnIndexOfPriceChangePercent24h)
          val _tmpHigh24h: Double
          _tmpHigh24h = _stmt.getDouble(_columnIndexOfHigh24h)
          val _tmpLow24h: Double
          _tmpLow24h = _stmt.getDouble(_columnIndexOfLow24h)
          val _tmpPage: Int
          _tmpPage = _stmt.getLong(_columnIndexOfPage).toInt()
          val _tmpFetchedAt: Long
          _tmpFetchedAt = _stmt.getLong(_columnIndexOfFetchedAt)
          _item = CoinMarketEntity(_tmpId,_tmpSymbol,_tmpName,_tmpImageUrl,_tmpCurrentPrice,_tmpMarketCap,_tmpMarketCapRank,_tmpPriceChangePercent24h,_tmpHigh24h,_tmpLow24h,_tmpPage,_tmpFetchedAt)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun count(): Int {
    val _sql: String = "SELECT COUNT(*) FROM coin_markets"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _result: Int
        if (_stmt.step()) {
          val _tmp: Int
          _tmp = _stmt.getLong(0).toInt()
          _result = _tmp
        } else {
          _result = 0
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteByPage(page: Int) {
    val _sql: String = "DELETE FROM coin_markets WHERE page = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, page.toLong())
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteAll() {
    val _sql: String = "DELETE FROM coin_markets"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public companion object {
    public fun getRequiredColumnConverters(): List<KClass<*>> = emptyList()

    public fun getRequiredDaoReturnTypeConverters(): List<KClass<*>> = emptyList()
  }
}
